import Handlebars from 'handlebars';
import Skeleton from '@mui/material/Skeleton';
import { gameTypeToUrlPrefix } from '@loyalty/shared-common/premium-games-api-client';
import { getLocalizedProperty, resolveAdvancedFormatConfigId } from '@loyalty/shared-common';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AdvancedFormatComponent } from './AdvancedFormatComponent';
import { AdvancedFormatComponentAttributes } from '../../../grapes-components-types/advanced-format-types';
import { CardLayouts } from '../campaign-card/layouts';
import { Channel } from '../campaign-card/apis/feed-api-client';
import { LayoutApiClient } from '../campaign-card/apis/layout-api-client';

if (!Handlebars.helpers.formatDate) {
  Handlebars.registerHelper('formatDate', (date: string, formatOptions: unknown) => {
    try {
      const options = JSON.parse(String(formatOptions));
      return Intl.DateTimeFormat('fr-FR', options).format(new Date(date));
    } catch (error) {
      console.error('Error parsing end date :', error);
      return date;
    }
  });
}

const SYNTHETIC_CHANNEL_ID = 0;

function buildAdvancedFormatUrl(
  attributes: AdvancedFormatComponentAttributes,
  {
    programId,
    lang,
    configId,
    templateId,
    isInternalUser,
  }: {
    programId: string;
    lang: string;
    configId: string;
    templateId: string;
    isInternalUser: boolean;
  },
): string {
  const { gameType } = attributes;
  if (!gameType) {
    throw new Error('Game type is required');
  }
  const urlPrefix = gameTypeToUrlPrefix[gameType];
  if (!urlPrefix) {
    throw new Error('Invalid game type');
  }
  const params = new URLSearchParams({
    program_id: programId,
    lang,
    config_id: configId,
    context_id: templateId,
  });
  if (isInternalUser) {
    params.set('internalUser', 'true'); // will be used to avoid cache
  }
  return `/advanced_formats/${urlPrefix}/?${params}`;
}

/**
 * Campaign-card-like surface that opens the advanced format game in a new tab.
 */
export function AdvancedFormatNewTabCard({
  isInternalUser,
  programId,
  templateId,
  locale,
  attributes,
  i18nAttributes,
  isEditorPreview,
}: {
  isInternalUser: boolean;
  programId: string;
  templateId: string;
  locale: string;
  attributes: AdvancedFormatComponentAttributes;
  i18nAttributes?: AdvancedFormatComponent['i18nAttributes'];
  isEditorPreview: boolean;
}) {
  const layoutId = attributes.template || CardLayouts.DEFAULT;
  const { data: layout } = useQuery({
    queryKey: ['advanced-format-card-layout', layoutId],
    queryFn: () => new LayoutApiClient().getLayout(layoutId),
  });
  const { data: translations } = useQuery({
    queryKey: ['advanced-format-card-translations', locale],
    queryFn: () => new LayoutApiClient().getTranslations(locale || 'en'),
  });

  const localizedEntity = { i18nAttributes };
  const title = getLocalizedProperty(localizedEntity, 'title', locale);
  const image = getLocalizedProperty(localizedEntity, 'image', locale);
  const description = getLocalizedProperty(localizedEntity, 'description', locale);
  const ctaLabel = getLocalizedProperty(localizedEntity, 'ctaLabel', locale);

  // Campaign card layouts are compiled with `{ channel }`. There is no feed/API channel here—only
  // visuals from component i18n attributes—so we build a Channel-shaped object with placeholders for the
  // required ids and reuse the same template variables (title, description, picture) as real cards.
  const visualInfoAsChannel: Channel = useMemo(
    () => ({
      uuid: 'advanced-format',
      campaignId: SYNTHETIC_CHANNEL_ID,
      title: title?.trim() || ' ',
      campaignType: 'advanced-format',
      description: description?.trim() || '',
      picture: image,
    }),
    [title, description, image],
  );

  const ref = useRef<HTMLDivElement>(null);

  const { mutate: openInNewTab } = useMutation({
    mutationKey: [
      'advanced-format-open-new-tab',
      programId,
      locale,
      attributes.configId,
      attributes.configUrl,
      attributes.gameType,
    ],
    mutationFn: async () => {
      const w = window.open('about:blank', '_blank');
      if (!w) {
        throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
      }
      w.opener = null;
      const configId = resolveAdvancedFormatConfigId(attributes);
      if (!configId) {
        w.close();
        throw new Error('Game is required');
      }
      w.location.href = buildAdvancedFormatUrl(attributes, {
        programId,
        isInternalUser,
        lang: locale,
        configId,
        templateId,
      });
      return;
    },
  });

  const onCta = useCallback(() => {
    if (isEditorPreview) {
      return;
    }
    openInNewTab();
  }, [isEditorPreview, openInNewTab]);

  const template = useMemo(() => {
    if (!layout) {
      return '';
    }
    return `
    ${layout.css?.trim() ? `<style>${layout.css}</style>` : ''}
    ${layout.js?.trim() ? `<script>${layout.js}</script>` : ''}
    ${layout.template}
  `;
  }, [layout]);

  const html = useMemo(() => {
    if (!layout || !translations) {
      return '';
    }
    const overridesTranslations = {
      ...translations,
      participate: ctaLabel || translations.participate,
    };
    return Handlebars.compile(template)({
      channel: visualInfoAsChannel,
      isFuture: false,
      isPast: false,
      isOpen: true,
      translations: overridesTranslations,
    });
  }, [layout, template, visualInfoAsChannel, ctaLabel, translations]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const handleClick = () => onCta();
    const ctaElements = ref.current.querySelectorAll<HTMLElement>('[data-qlf-cta]');
    ctaElements.forEach(cta => cta.addEventListener('click', handleClick));
    return () => {
      ctaElements.forEach(cta => cta.removeEventListener('click', handleClick));
    };
  }, [onCta, html]);

  if (!layout || !translations) {
    return <Skeleton variant="rectangular" height={420} style={{ width: '100%' }} />;
  }

  return (
    <div
      ref={ref}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ height: '100%' }}
      className="qlf-advanced-format-card"
    />
  );
}
